import { ArrowRight, Building2, Check, Landmark, Lock, Plus, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Section8Case, useSection8 } from "@/hooks/use-section8";
import { useSection8Eligibility } from "@/hooks/use-section8-eligibility";
import { toUserMessage } from "@/lib/supabase-errors";
import { cn } from "@/lib/utils";
import { ApprovalStep, EligibilityStep, MoveInStep, PHAStep, VoucherStep } from "./wizard/steps";
import { INITIAL, STEPS, availableProperties, deriveStatus, tenantEmailError } from "./wizard/data";
import type { Eligibility, EligibilityResult, SectionKey, WizardForm } from "./wizard/data";

const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
const str = (v: string) => (v.trim() === "" ? undefined : v.trim());

const NONE_SAVED: Record<SectionKey, boolean> = {
  eligibility: false, voucher: false, pha: false, approval: false, movein: false,
};

/**
 * Which sections an existing case counts as saved. Prefers the explicit
 * `saved_sections` the wizard persists; for cases created before that column
 * existed, falls back to inferring from which fields hold data.
 */
function savedFromCase(c: Section8Case): Record<SectionKey, boolean> {
  const ss = c.saved_sections;
  if (ss && ss.length > 0) {
    return STEPS.reduce((acc, s) => ({ ...acc, [s.key]: ss.includes(s.key) }), {} as Record<SectionKey, boolean>);
  }
  return {
    eligibility: !!c.pipeline_property_id,
    voucher: !!(c.tenant_phone || c.tenant_email || c.tenant_voucher_amount),
    pha: !!(c.pha_name || c.pha_phone || c.pha_email || c.asking_rent),
    approval: !!(c.payment_standard || c.utility_allowance || c.submission_date || c.approval_date),
    movein: !!(c.tenant_move_in_date || c.hap_contract_start),
  };
}

/** Hydrate the wizard form from an existing case (edit mode). */
function fromCase(c: Section8Case): WizardForm {
  const numStr = (n: number | null) => (n != null ? String(n) : "");
  // Prefer the stored toggle; legacy cases (column null) fall back to inference.
  const approved: WizardForm["approved"] =
    c.pha_approved === true ? "yes"
      : c.pha_approved === false ? "no"
      : c.approval_date || c.status === "approved" || c.status === "leased" ? "yes" : "";
  const hapStarted = c.hap_started ?? (!!c.hap_contract_start || c.status === "leased");
  return {
    tenantName: c.tenant_name ?? "",
    property: c.pipeline_property_id,
    salary: numStr(c.tenant_monthly_salary),
    tenantPhone: c.tenant_phone ?? "",
    tenantEmail: c.tenant_email ?? "",
    voucherAmount: numStr(c.tenant_voucher_amount),
    phaName: c.pha_name ?? "",
    phaPhone: c.pha_phone ?? "",
    phaEmail: c.pha_email ?? "",
    phaParticipantCode: c.pha_participant_code ?? "",
    askingRent: numStr(c.asking_rent),
    paymentStandard: numStr(c.payment_standard),
    utilityAllowance: numStr(c.utility_allowance),
    submissionDate: c.submission_date ?? "",
    approved,
    approvalDate: c.approval_date ?? "",
    moveInDate: c.tenant_move_in_date ?? "",
    hapStarted,
    hapStartDate: c.hap_contract_start ?? "",
  };
}

export function CaseWizardDialog({ editCase, open: controlledOpen, onOpenChange }: {
  /** When set, the dialog edits this case (controlled open) instead of creating. */
  editCase?: Section8Case | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
} = {}) {
  const { cases, createCase, updateCase, saveDraft } = useSection8();
  const { properties, checkEligibility } = useSection8Eligibility();
  const [internalOpen, setInternalOpen] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [active, setActive] = useState<SectionKey>("eligibility");
  const [eligibility, setEligibility] = useState<Eligibility>("idle");
  const [result, setResult] = useState<EligibilityResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [saved, setSaved] = useState<Record<SectionKey, boolean>>(NONE_SAVED);
  const [form, setForm] = useState<WizardForm>(INITIAL);

  const isEdit = !!editCase;
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const set = <K extends keyof WizardForm>(key: K, value: WizardForm[K]) => setForm((p) => ({ ...p, [key]: value }));

  // Hydrate from the case when an edit dialog opens; existing cases bypass the
  // eligibility gate (the tenant already qualified when the case was created).
  useEffect(() => {
    if (open && editCase) {
      setForm(fromCase(editCase));
      setEligibility("pass");
      setActive("eligibility");
      setSaved(savedFromCase(editCase));
      setDraftId(editCase.id);
    }
  }, [open, editCase]);

  const unlocked = isEdit || eligibility === "pass";
  const savedCount = Object.values(saved).filter(Boolean).length;
  const progress = Math.round((savedCount / STEPS.length) * 100);
  const activeIdx = STEPS.findIndex((s) => s.key === active);
  const isLast = activeIdx === STEPS.length - 1;
  // A property may hold only one case per workspace, so hide any that already
  // have one — except the property of the case currently being edited.
  const propertyOptions = useMemo(
    () => availableProperties(properties, cases.map((c) => c.pipeline_property_id), editCase?.pipeline_property_id),
    [properties, cases, editCase],
  );
  const selectedProperty = properties.find((p) => p.value === form.property);
  const selectedZip = selectedProperty?.zip ?? "";
  const submitting = createCase.isPending || updateCase.isPending;

  const resetAll = () => {
    setForm(INITIAL); setActive("eligibility"); setEligibility("idle"); setResult(null);
    setChecking(false); setSaved(NONE_SAVED); setDraftId(null);
  };

  const buildPayload = () => ({
    pipeline_property_id: form.property,
    status: deriveStatus(form),
    tenant_name: str(form.tenantName),
    tenant_phone: str(form.tenantPhone),
    tenant_email: str(form.tenantEmail),
    tenant_voucher_amount: num(form.voucherAmount),
    pha_name: str(form.phaName),
    pha_phone: str(form.phaPhone),
    pha_email: str(form.phaEmail),
    pha_participant_code: str(form.phaParticipantCode),
    asking_rent: num(form.askingRent),
    payment_standard: num(form.paymentStandard),
    utility_allowance: num(form.utilityAllowance),
    submission_date: str(form.submissionDate),
    approval_date: str(form.approvalDate),
    pha_approved: form.approved === "yes" ? true : form.approved === "no" ? false : undefined,
    tenant_move_in_date: str(form.moveInDate),
    tenant_monthly_salary: num(form.salary),
    hap_contract_start: str(form.hapStartDate),
    hap_started: form.hapStarted,
  });

  const handleOpenChange = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
    if (!next) resetAll();
  };

  const runCheck = async () => {
    setChecking(true);
    try {
      const r = await checkEligibility({ zip: selectedZip, monthlySalary: form.salary });
      setResult(r);
      setEligibility(r.status);
    } catch (e) {
      setEligibility("idle");
      setResult(null);
      toast.error(toUserMessage(e, "Eligibility check failed"));
    } finally {
      setChecking(false);
    }
  };

  const goTo = (key: SectionKey, idx: number) => {
    if (idx > 0 && !unlocked) return;
    setActive(key);
  };

  // Persist the section: upserts a draft (insert on first save, update after)
  // so the case survives a closed dialog and resumes from the cases list.
  const saveSection = (key: SectionKey) => {
    if (!form.property) {
      setActive("eligibility");
      toast.error("Select a property before saving");
      return;
    }
    if (tenantEmailError(form.tenantEmail)) {
      setActive("voucher");
      toast.error("Enter a valid tenant email before saving");
      return;
    }
    const idx = STEPS.findIndex((s) => s.key === key);
    const nextSaved = { ...saved, [key]: true };
    const savedSections = STEPS.filter((s) => nextSaved[s.key]).map((s) => s.key);
    saveDraft.mutate(
      { id: draftId ?? editCase?.id, ...buildPayload(), saved_sections: savedSections },
      {
        onSuccess: (id) => {
          setDraftId(id);
          setSaved(nextSaved);
          if (idx < STEPS.length - 1 && unlocked) setActive(STEPS[idx + 1].key);
        },
      },
    );
  };

  const handleSubmit = () => {
    if (!form.property) { setActive("eligibility"); return; }
    if (tenantEmailError(form.tenantEmail)) {
      setActive("voucher");
      toast.error("Enter a valid tenant email before continuing");
      return;
    }
    const caseId = editCase?.id ?? draftId;
    const onSuccess = () => handleOpenChange(false);
    // Creating commits the whole case (all sections); editing saves only the
    // section in view — it marks that one saved without flipping the others.
    const nextSaved = { ...saved, [active]: true };
    const savedSections = isEdit
      ? STEPS.filter((s) => nextSaved[s.key]).map((s) => s.key)
      : STEPS.map((s) => s.key);
    const payload = { ...buildPayload(), saved_sections: savedSections };
    if (caseId) updateCase.mutate({ id: caseId, ...payload }, { onSuccess });
    else createCase.mutate(payload, { onSuccess });
  };

  const propertyLabel = selectedProperty?.label;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Case</Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-3xl gap-0 p-0">
        {/* Header */}
        <DialogHeader className="space-y-2 border-b border-border px-6 pb-4 pt-6">
          <div className="flex items-center gap-2.5">
            <DialogTitle>{isEdit ? "Edit Section 8 Case" : "Create Section 8 Case"}</DialogTitle>
            {!isEdit && <Badge variant="outline" className="text-[11px] font-medium text-muted-foreground">Draft</Badge>}
          </div>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
              <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>Property:{" "}
                {propertyLabel
                  ? <span className="font-medium text-foreground">{propertyLabel}</span>
                  : <span className="text-muted-foreground/70">— select in Eligibility —</span>}
              </span>
              <span className="text-border">·</span>
              <Landmark className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>PHA:{" "}
                {form.phaName
                  ? <span className="font-medium text-foreground">{form.phaName}</span>
                  : <span className="text-muted-foreground/70">— add in PHA step —</span>}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Step tabs */}
        <div className="flex gap-1.5 px-6 pt-4">
          {STEPS.map((tab, idx) => {
            const isActive = tab.key === active;
            const isDone = saved[tab.key];
            const isLocked = idx > 0 && !unlocked;
            return (
              <button key={tab.key} type="button" onClick={() => goTo(tab.key, idx)} disabled={isLocked}
                className={cn("flex flex-1 items-center gap-2 border-b-2 pb-2.5 transition-colors",
                  isActive ? "border-primary" : "border-border",
                  isLocked ? "cursor-not-allowed" : "cursor-pointer")}>
                <span className={cn("flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                  isActive ? "bg-primary text-primary-foreground"
                    : isDone ? "bg-emerald-500/15 text-emerald-500"
                    : "bg-muted text-muted-foreground")}>
                  {isDone ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                    : isLocked ? <Lock className="h-3 w-3" strokeWidth={2} />
                    : idx + 1}
                </span>
                <span className={cn("hidden text-[13px] font-semibold sm:inline",
                  isActive ? "text-foreground" : isLocked ? "text-muted-foreground/60" : "text-muted-foreground")}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Progress */}
        <div className="flex items-center gap-3 px-6 pt-3.5">
          <Progress value={progress} className="h-1.5" />
          <span className="shrink-0 text-xs font-medium text-muted-foreground">{savedCount} of {STEPS.length} saved · {progress}%</span>
        </div>

        {/* Body */}
        <div className="max-h-[55vh] overflow-y-auto px-6 pb-2 pt-5">
          {active === "eligibility" && <EligibilityStep form={form} set={set} properties={propertyOptions} eligibility={eligibility} result={result} checking={checking} onCheck={runCheck} />}
          {active === "voucher" && <VoucherStep form={form} set={set} />}
          {active === "pha" && <PHAStep form={form} set={set} phaState={selectedProperty?.state} phaLat={selectedProperty?.lat} phaLng={selectedProperty?.lng} />}
          {active === "approval" && <ApprovalStep form={form} set={set} />}
          {active === "movein" && <MoveInStep form={form} set={set} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <Button type="button" variant="outline" size="sm" onClick={() => saveSection(active)}
            disabled={(active === "eligibility" && !unlocked) || saveDraft.isPending}>
            {saved[active] ? <Check className="mr-1.5 h-4 w-4 text-emerald-500" strokeWidth={2.5} /> : <Save className="mr-1.5 h-4 w-4" strokeWidth={1.75} />}
            {saved[active] ? "Saved" : "Save section"}
          </Button>
          {isEdit ? (
            <Button type="button" size="sm" onClick={handleSubmit} disabled={submitting}>
              <Check className="mr-1.5 h-4 w-4" strokeWidth={2.5} /> Save changes
            </Button>
          ) : isLast ? (
            <Button type="button" size="sm" onClick={handleSubmit} disabled={submitting}>
              <Check className="mr-1.5 h-4 w-4" strokeWidth={2.5} /> Create case
            </Button>
          ) : (
            <Button type="button" size="sm" disabled={!unlocked}
              onClick={() => goTo(STEPS[activeIdx + 1].key, activeIdx + 1)}>
              Continue to {STEPS[activeIdx + 1].label}
              <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={2} />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
